import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';

import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { HrService } from '../hr.service';
import { CommonService } from '@app/common/common.service';
import { AlertService } from '@app/shared/alert.service';
import {
	HrActivity, HrEmployee, HrSchedule, hrColorOf, hrEmployeeNameOf, hrOpeningOf, hrResolveActivities, hrScheduleActivityId,
} from '@app/shared/_models/hr';
import { Store, User } from '@app/shared/_models/common';
import { HR_PERSON_COLORS } from '@app/shared/constants';
import {
	SchedulePeriodStatus, comparePeriods, minutesLabel, schedulePeriodLabel, schedulePeriodStatus,
} from '../hr-schedule-engine';
import { mergeRanges } from '../hr-timeline';
import { rosterOrderOf } from '../hr-roster';
import { HrPrintService, hrPrintErrorMessage } from '../hr-print.service';
import { SchedulePrintGrid, buildSchedulePrintDoc } from '../hr-schedule-print';
import { todayStr } from '@app/shared/time';

// Ce que l'on veut voir : ce qui sert (en cours + à venir), ou les archives, ou tout.
export type ScheduleScope = 'live' | 'past' | 'all';

interface ScheduleRow {
	id: number;
	name: string;
	store: string;
	activity: string;
	period: string;
	status: SchedulePeriodStatus;
	statusLabel: string;
	cycle: string;
	members: number;
	active: boolean;
}

const STATUS_LABELS: { [k in SchedulePeriodStatus]: string } = { past: 'Passé', current: 'En cours', future: 'À venir' };

// Liste de TOUS les horaires types — /hr/schedules. Un même service accumule une grille par période
// (des centaines au fil des années) : on n'affiche donc par défaut que ce qui sert encore — en cours
// et à venir — les archives restant à un clic.
@Component({
	selector: 'app-hr-schedules',
	standalone: true,
	imports: [MenuCommonComponent, MatTableModule, MatFormFieldModule, MatInputModule, MatButtonModule,
		MatButtonToggleModule, MatIconModule, MatTooltipModule],
	templateUrl: './schedules.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './schedules.component.scss',
})
export class SchedulesComponent implements OnInit {

	public hrService = inject(HrService);
	private commonService = inject(CommonService);
	private alertService = inject(AlertService);
	private printService = inject(HrPrintService);
	private router = inject(Router);

	public displayedColumns: string[] = ['name', 'store', 'activity', 'period', 'status', 'cycle', 'members', 'active'];
	public dataSource: MatTableDataSource<ScheduleRow> = new MatTableDataSource<ScheduleRow>();
	public loaded = false;
	public scope: ScheduleScope = 'live';
	public counts: { live: number; past: number; all: number } = { live: 0, past: 0, all: 0 };

	private rows: ScheduleRow[] = [];
	private search = '';
	// Gardés pour l'IMPRESSION : la liste n'affiche que des libellés, le document a besoin des
	// créneaux, du roster et des fiches salariés.
	private schedules: HrSchedule[] = [];
	private activities: HrActivity[] = [];
	private stores: Store[] = [];
	private employees: HrEmployee[] = [];
	private users: User[] = [];

	ngOnInit(): void {
		forkJoin({
			schedules: this.hrService.getSchedules$(),
			activities: this.hrService.getActivities$(),
			stores: this.commonService.getStores(),
			employees: this.hrService.getEmployees$(),
			users: this.hrService.getUsers$(),
		}).subscribe(({ schedules, activities, stores, employees, users }) => {
			const resolved = hrResolveActivities(activities, schedules);
			const dat = todayStr();
			this.schedules = schedules;
			this.activities = resolved;
			this.stores = stores;
			this.employees = employees;
			this.users = users;
			// En cours d'abord, puis ce qui vient (au plus tôt), puis les archives (au plus récent).
			this.rows = [...schedules]
				.sort((a, b) => {
					const ra = this.rank(a, dat), rb = this.rank(b, dat);
					if (ra !== rb) return ra - rb;
					return ra === 2 ? -comparePeriods(a, b) : comparePeriods(a, b);
				})
				.map(s => this.toRow(s, stores, resolved, dat));
			this.counts = {
				live: this.rows.filter(r => r.status !== 'past').length,
				past: this.rows.filter(r => r.status === 'past').length,
				all: this.rows.length,
			};
			this.applyScope();
			this.loaded = true;
		});
	}

	private rank(s: HrSchedule, dat: string): number {
		const st = schedulePeriodStatus(s, dat);
		return st === 'current' ? 0 : (st === 'future' ? 1 : 2);
	}

	private toRow(s: HrSchedule, stores: Store[], activities: HrActivity[], dat: string): ScheduleRow {
		const status = schedulePeriodStatus(s, dat);
		return {
			id: s.id,
			name: s.name,
			store: stores.find(st => st.id === s.id_store)?.name ?? '',
			activity: activities.find(a => a.id === hrScheduleActivityId(s))?.name ?? '',
			period: schedulePeriodLabel(s),
			status,
			statusLabel: STATUS_LABELS[status],
			cycle: s.cycle_weeks > 1 ? (s.cycle_weeks + ' semaines' + (s.cycle_weeks === 2 ? ' (alternance)' : '')) : 'Fixe',
			members: (s.members ?? []).length,
			active: s.is_active !== false,
		};
	}

	// Le filtre de portée agit sur les DONNÉES (pas sur le filtre texte de MatTableDataSource, qui
	// reste à l'utilisateur) : les deux se cumulent sans se marcher dessus.
	private applyScope(): void {
		const keep = this.scope === 'all' ? this.rows
			: this.rows.filter(r => (this.scope === 'past' ? r.status === 'past' : r.status !== 'past'));
		this.dataSource = new MatTableDataSource(keep);
		this.dataSource.filter = this.search;
	}

	onScope(scope: ScheduleScope): void {
		this.scope = scope;
		this.applyScope();
	}

	applyFilter(event: Event) {
		this.search = (event.target as HTMLInputElement).value.trim().toLowerCase();
		this.dataSource.filter = this.search;
	}

	onClickRow(row: ScheduleRow): void {
		this.router.navigate(['/hr/schedule/' + row.id]);
	}

	// « Nouvel emploi du temps » doit AMENER SUR UNE GRILLE VIERGE : /hr/schedule/0 rouvrait la
	// grille existante du premier service (aucun id 0 en base, donc repli sur le service courant).
	onNew(): void {
		this.router.navigate(['/hr/schedule/new']);
	}

	// ============================================================
	// Impression — « les horaires de l'entreprise », en un seul document
	// ============================================================
	//
	// On imprime CE QUI EST AFFICHÉ (portée + recherche) : demander « les horaires en cours et à
	// venir du magasin » se fait avec les filtres de l'écran, pas avec une boîte de dialogue de
	// plus. Chaque grille part sur une page neuve, avec ses semaines de roulement.
	onPrintAll(): void {
		const shown = this.dataSource.filteredData;
		const grids = shown
			.map(row => this.schedules.find(s => s.id === row.id))
			.filter((s): s is HrSchedule => !!s)
			.map(s => this.printGridOf(s));

		if (grids.length === 0) {
			this.alertService.error('Aucune grille affichée à imprimer.');
			return;
		}
		const today = todayStr();
		// Un seul établissement affiché ⇒ on le nomme ; sinon le document couvre l'organisation.
		const storeNames = Array.from(new Set(grids.map(g => g.storeName).filter(Boolean)));
		const doc = buildSchedulePrintDoc({
			storeName: storeNames.length === 1 ? storeNames[0] : 'Tous les magasins',
			printedAt: today.slice(8, 10) + '/' + today.slice(5, 7) + '/' + today.slice(0, 4),
			today,
			grids,
		});
		// Rendu par le SERVEUR (POST /hr/print) : la fenêtre s'ouvre dans le prolongement du clic.
		this.printService.print(doc).subscribe({
			error: (e) => this.alertService.error(hrPrintErrorMessage(e)),
		});
	}

	private printGridOf(sch: HrSchedule): SchedulePrintGrid {
		const activity = this.activities.find(a => a.id === hrScheduleActivityId(sch));
		const name = (id: number) => hrEmployeeNameOf(this.employees.find(e => e.id === id), this.users);
		// Membres du roster ET porteurs de créneaux : un créneau d'une personne retirée du roster
		// est encore planifié — il doit sortir sur le papier.
		const ids = Array.from(new Set([...(sch.members ?? []), ...(sch.lnk_slots ?? []).map(s => s.id_employee)]));
		const order = rosterOrderOf(ids, sch.data, rest => rest.slice().sort((a, b) => name(a).localeCompare(name(b))));

		return {
			name: sch.name,
			storeName: this.stores.find(s => s.id === sch.id_store)?.name ?? '',
			activityName: activity?.name ?? sch.name,
			cycle_weeks: Math.max(1, sch.cycle_weeks),
			anchor_monday: sch.anchor_monday,
			datstart: sch.datstart,
			datend: sch.datend,
			is_active: sch.is_active,
			slots: (sch.lnk_slots ?? []).map(s => ({
				id_employee: s.id_employee,
				week_index: s.week_index,
				weekday: s.weekday,
				start_min: s.start_min,
				end_min: s.end_min,
				break_min: s.break_min ?? 0,
			})),
			members: order.map(id => {
				const emp = this.employees.find(e => e.id === id);
				return {
					id,
					name: name(id),
					color: hrColorOf(id, HR_PERSON_COLORS),
					contract_min: emp?.contract_hours_week != null ? Math.round(emp.contract_hours_week * 60) : null,
					cycle_contract_min: emp?.cycle_hours?.length ? emp.cycle_hours.map(h => Math.round(h * 60)) : null,
				};
			}),
			openHours: [1, 2, 3, 4, 5, 6, 7].map(d => mergeRanges(hrOpeningOf(activity, d))
				.map(o => minutesLabel(o.start_min) + ' – ' + minutesLabel(o.end_min))
				.join(', ')),
		};
	}
}
